import { ALG } from "./ALG";
import { BookingType } from "./BookingType";
import { BatchingTableGroups, Table } from "./Box";
import { ButlerType } from "./Enums";
import { Guest } from "./Guest";
import { IHash } from "./IHash";
import { PickupDeliverySchedule } from "./PickupDeliverySchedule";
import { CashPaymentSetting, PaymentSummary } from "./transaction";

export interface GuestEmailData {
  customerId: string; // customer to send email to
  isRsvpSent: boolean;
  inviteFamily: boolean;
  invitationMessage: string;
  invitationImage: any;
  rsvpLink?: string;
  invitationManagementLink?: string;
  isNoOneAttending?: boolean;
  groupName?: string;
  allCustomerName?: string;
  numberOfAdultGuest?: number;
  numberOfChildrenGuest?: number;
  numberOfAdultGuestAttending?: number;
  numberOfChildrenGuestAttending?: number;
  numberOfAdultGuestNotAttending?: number;
  numberOChildrenGuestNotAttending?: number;
  attendingGuests?: Guest[];
  notAttendingGuests?: Guest[];
  isUserAttending: boolean; // is email customer attending
}

export interface Category {
  productName: string;
  productPrice: string;
  productSizeName: string;
  productId: string;
  productSizeId: string;
  quantity: number;
  subcategoryId: string;
  butlerTypeId: ButlerType;
  text: string[];
  categoryId: string;
  requiredPrepayment?: boolean;
  messages?: string[];
}

export enum UpdateStatus {
  none = 0,
  updating = 1,
  updated = 2,
}

export interface Bookings {
  [bookingId: string]: Booking;
}

export interface BookingBase {
  _key?: string;
  date: number;
  mealId?: string;
  widgetId: string;
  restaurantId: string;
  pax: number;
  customerId?: string;
  index?: number;
  status?: number;
  highlight?: boolean;
  enableOnlineOrdering?: boolean;
  menuId?: string;
  menuOptionId?: string;
  guests?: Guest[];
  intervalId?: number;
  dateCreated?: number;
  standBy?: number;
  qrMobile?: string;
  qrMobileCode?: string;
  cancellationDate?: number;
  standByPriority?: number;
  children?: number;
  highChair?: number;
  groupAreaId?: string;
  seatingUnlock?: boolean;
  shortLink?: string;
  type?: BookingType;
  tableOverride?: string[];
  combineFixedTables?: boolean;
  combineSectionTables?: boolean;
  endTime?: number;
  paxLimitsOverride?: boolean;
  sectionLimitOverride?: boolean;
  extraPax?: number;
  customerNote?: string;
  staffNote?: string;
  referralNote?: string;
  alternateCustomerId?: string;
  madeByNotAttending?: boolean;
  statusChangeData?: {
    Confirmed?: number;
    Cancelled?: number;
    NoShow?: number;
    Vacated?: number;
    Seated?: number;
    Arrived?: number;
  };
  alg?: ALG;
  batchingTableGroups?: BatchingTableGroups;
  schedule?: PickupDeliverySchedule;
  payLater?: boolean;
  cashPayment?: boolean;
  cashPaymentSetting?: CashPaymentSetting;
  ccOverride?: boolean;
  tableNumber?: string; // primary table number
  organizerSeat?: string;
}

export interface Booking extends BookingBase {
  mealId: string; // Made mandatory
  uid?: string;
  onMyWay?: number;
  continueTheWaitlist?: boolean;
  courseId?: string;
  updating?: UpdateStatus;
  notAttending?: Guest[];
  lastUpdated?: number;
  casual?: boolean;
  casualToQR?: boolean;
  standByIntervalId?: number;
  received?: { [k: string]: { productId: string; productSizeId: string } };
  standByQuote?: number;
  extendDepartureTime?: string;
  standByBuffer?: number;
  invitationMessage?: string;
  invitationImage?: InvitationImage;
  creditCardDetailsConfirmed?: boolean;
  manualPaymentConfirmed?: boolean;
  tableClassId?: string;
  qrCodeLink?: string;
  reference?: string;
  areaId?: string; //area preference
  extendDurationId?: string;
  butlerPreferences?: {
    personalisationNote: string;
    wineRange: number[];
    dessertMessage: string;
  };
  dateCreatedFormatted?: any;
  dateFormatted?: any;
  adultBeveragePackageId?: string;
  adultBeveragePackageProductId?: string;
  childBeveragePackageId?: string;
  childBeveragePackageProductId?: string;
  expressMobile?: string;
  expressMobileCode?: string;
  expressFirstName?: string;
  expressLastName?: string;
  categories?: Category[];
  casualName?: string;
  referralId?: string;
  allergies?: Array<{ label: string; value: string }>;
  dietaryRequirements?: Array<{ label: string; value: string }>;
  occasionId?: string;
  paymentSummary?: PaymentSummary;
  noShow?: {
    enabled?: boolean;
    adults?: number;
    children?: number;
  };
  cancelled?: {
    enabled?: boolean;
    adults?: number;
    children?: number;
  };
}

export interface AssignmentSection {
  order: {
    [boxId: string]: number;
  };
  joins: IHash<Join>;
  reserves: {
    [boxId: string]: Table;
  };
  heads?: {
    boxId: string;
    left: number;
    right: number;
  }[];
}

export interface Assignment {
  restaurantId: string;
  sections: {
    [sectionId: string]: AssignmentSection;
  };
}

export interface Assignments {
  [assignmentId: string]: Assignment;
}

export interface Join {
  bookingId?: string;
  enabled: boolean;
  initial?: boolean;
  orphan?: boolean;
  tables: {
    [boxId: string]: boolean;
  };
  spacing?: number;
}

export type InvitationImage = {
  url: string;
  thumbUrl: string;
  sizeFile: number;
  sizeThumb: number;
  fileName: string;
  name: string;
  type: SupportedImageMimeType;
  ext: string;
};

export enum SupportedImageMimeType {
  Jpg = "image/jpeg",
  Png = "image/png",
}
